import { Observable, of } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

interface BdAccountScopedCacheEntry<T> {
  accountId: string | null;
  permissions: T;
}

/**
 * Session-storage cache for a permission list, scoped to the signed-in account.
 *
 * An entry records the account it was fetched for, and validity is checked when
 * the entry is *read* rather than by reacting to authentication events. Both
 * halves of that matter (BDA-6069):
 *
 * - MSAL reports an account on every page load, so event-driven invalidation
 *   wiped the entry before the first read. The cache never survived a reload
 *   and every page load re-requested the permission endpoints.
 * - Authentication events arrive *after* consumers start reading —
 *   `authentication$` flips one synchronous line before `accountInfo$` emits —
 *   so event-driven invalidation can hand out the previous account's
 *   permissions and only clear them afterwards.
 *
 * Reading against the current account id closes both: same account means reuse,
 * a different account is a miss and triggers a fetch.
 */
export class BdAccountScopedPermissionCache<T extends readonly string[]> {
  private _inFlight: Observable<T> | undefined;
  private _inFlightAccountId: string | null | undefined;

  /**
   * @param _isBrowser sessionStorage is browser-only; during SSR every read is
   * a miss and writes are dropped instead of throwing a ReferenceError.
   */
  constructor(
    private readonly _storageKey: string,
    private readonly _isBrowser: boolean,
  ) {}

  /**
   * Resolves the permission list for `accountId`, fetching only when no entry
   * for that account exists. `force` skips the stored entry but still shares an
   * in-flight request, which is what the previous implementation did.
   */
  public resolve(
    accountId: string | null,
    force: boolean,
    fetch: () => Observable<T>,
  ): Observable<T> {
    if (this._inFlightAccountId !== accountId) {
      this._inFlight = undefined;
      this._inFlightAccountId = accountId;
    }

    if (!force) {
      const stored = this._read(accountId);
      if (stored) {
        return of(stored);
      }
    }

    if (!this._inFlight) {
      this._inFlight = fetch().pipe(
        tap((permissions) => this._write(accountId, permissions)),
        shareReplay(1),
      );
    }

    return this._inFlight;
  }

  private _read(accountId: string | null): T | undefined {
    if (!this._isBrowser) {
      return undefined;
    }

    const raw = sessionStorage.getItem(this._storageKey);
    if (!raw) {
      return undefined;
    }

    const entry = this._parse(raw);
    if (!entry || entry.accountId !== accountId) {
      return undefined;
    }

    return entry.permissions;
  }

  private _write(accountId: string | null, permissions: T): void {
    if (!this._isBrowser) {
      return;
    }

    const entry: BdAccountScopedCacheEntry<T> = { accountId, permissions };
    sessionStorage.setItem(this._storageKey, JSON.stringify(entry));
  }

  /**
   * Returns `undefined` for anything this class did not write — including the
   * bare permission array stored by earlier versions, which is a cache miss and
   * therefore refetched once per session after a deployment.
   */
  private _parse(raw: string): BdAccountScopedCacheEntry<T> | undefined {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return undefined;
    }

    if (typeof parsed !== 'object' || parsed === null) {
      return undefined;
    }
    if (!('accountId' in parsed) || !('permissions' in parsed)) {
      return undefined;
    }

    let accountId: string | null;
    if (typeof parsed.accountId === 'string') {
      accountId = parsed.accountId;
    } else if (parsed.accountId === null) {
      accountId = null;
    } else {
      return undefined;
    }

    if (!Array.isArray(parsed.permissions)) {
      return undefined;
    }
    const permissions: unknown[] = parsed.permissions;
    if (
      !permissions.every((value): value is string => typeof value === 'string')
    ) {
      return undefined;
    }

    // The checks above prove `string[]`; `T` narrows that to the concrete
    // permission union, which JSON cannot carry.
    return { accountId, permissions: permissions as unknown as T };
  }
}
